import { useState, useMemo } from 'react';
import { usePaperWriter } from '../PaperWriterContext';
import { PAPER_TEMPLATES, type PaperTemplate } from '../paperWriterConstants';

const CATEGORIES = ['All', 'Literature Review', 'Case Study', 'Research Paper', 'Essay', 'Thesis', 'Report'];

export default function TemplatesTab() {
  const { setTopicDescription, setPaperOptions, paperOptions, setActiveTab } = usePaperWriter();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [appliedId, setAppliedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return PAPER_TEMPLATES.filter((t) => {
      const matchCat = selectedCategory === 'All' || t.category === selectedCategory;
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        t.title.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.tags.some((tag) => tag.toLowerCase().includes(q));
      return matchCat && matchSearch;
    });
  }, [selectedCategory, search]);

  const handleUseTemplate = (template: PaperTemplate) => {
    setTopicDescription(template.topic);
    setPaperOptions({
      ...paperOptions,
      paperType: template.paperType,
      academicLevel: template.academicLevel,
      citationStyle: template.citationStyle,
      wordCount: template.wordCount,
      tone: template.tone,
    });
    setAppliedId(template.id);
    setTimeout(() => {
      setActiveTab('upload');
    }, 800);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex-shrink-0">
          <i className="ri-layout-grid-line text-white text-lg"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-extrabold text-gray-900">Paper Templates</p>
          <p className="text-xs text-gray-500">Pre-filled prompts for common academic paper types</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search templates…"
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 transition-all"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 cursor-pointer transition-all"
          >
            <i className="ri-close-line text-xs"></i>
          </button>
        )}
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
              selectedCategory === cat
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300 hover:text-emerald-700'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Results count */}
      <p className="text-xs text-gray-400 font-medium">
        {filtered.length} template{filtered.length !== 1 ? 's' : ''} found
      </p>

      {/* Template Cards */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gray-100 mb-3">
              <i className="ri-search-line text-gray-400 text-2xl"></i>
            </div>
            <p className="text-sm font-bold text-gray-600 mb-1">No templates found</p>
            <p className="text-xs text-gray-400">Try a different search or category</p>
          </div>
        ) : (
          filtered.map((template) => {
            const isApplied = appliedId === template.id;
            return (
              <div
                key={template.id}
                className="group bg-white border border-gray-100 rounded-2xl p-4 hover:border-emerald-200 hover:shadow-md transition-all"
              >
                {/* Top row */}
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${template.categoryBg} flex-shrink-0`}>
                    <i className={`${template.icon} ${template.categoryColor} text-base`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-gray-900 leading-tight mb-1">
                      {template.title}
                    </p>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${template.categoryBg} ${template.categoryColor}`}>
                      {template.category}
                    </span>
                  </div>
                </div>

                {/* Description */}
                <p className="text-xs text-gray-500 leading-relaxed mb-3">{template.description}</p>

                {/* Meta badges */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {[template.academicLevel, template.citationStyle, template.wordCount + ' words'].map((badge) => (
                    <span
                      key={badge}
                      className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500"
                    >
                      {badge}
                    </span>
                  ))}
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {template.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Use Template Button */}
                <button
                  onClick={() => handleUseTemplate(template)}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                    isApplied
                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                      : 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:shadow-md hover:shadow-emerald-100'
                  }`}
                >
                  <i className={`${isApplied ? 'ri-check-line' : 'ri-magic-line'} text-sm`}></i>
                  {isApplied ? 'Template Applied! Redirecting…' : 'Use This Template'}
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
